import axios, { AxiosError, type InternalAxiosRequestConfig } from "axios";

import { useAuthStore } from "@/store/auth-store";

// The app is served under a prefix ("/sonoki/"), so anything root-relative
// has to carry it. Vite's ``base`` lands in BASE_URL (with a trailing slash),
// which makes it the one place the prefix is declared. The VITE_API_BASE
// override stays for the production image build; the fallback below is
// already correct, so dev needs nothing set.
const BASE = import.meta.env.BASE_URL; // "/sonoki/"
const API_BASE = import.meta.env.VITE_API_BASE ?? `${BASE}api`;
const LOGIN_PATH = `${BASE}login`;

// PHI workload: JWTs live in memory only — never localStorage / sessionStorage,
// because XSS would otherwise lift them. Refresh tokens travel as HttpOnly,
// SameSite=Strict cookies set by the backend.
export const apiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
  timeout: 15_000,
});

// Same baseURL but no auth interceptor — for endpoints the login page
// can hit before the user has signed in (e.g. ``/organisation/public``
// for the org logo + name on the login screen).
export const publicApiClient = axios.create({
  baseURL: API_BASE,
  withCredentials: false,
  timeout: 15_000,
});

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().accessToken;
  if (token) {
    config.headers.set("Authorization", `Bearer ${token}`);
  }
  return config;
});

/**
 * Why this is three-valued and not just "did we get a token".
 *
 * A failed refresh has two very different causes and they need opposite
 * handling:
 *
 *   - The server actively rejected the session (401/403): the refresh token is
 *     gone, revoked, or replayed. The user really is logged out.
 *   - The server could not be reached at all — restarted container, proxy
 *     blip, timeout, 502. The session is almost certainly still valid.
 *
 * The original code caught everything and returned null, so both paths cleared
 * auth and hard-navigated to /login. That means a backend restart silently
 * throws every sonographer back to the login screen, mid-worksheet, with
 * whatever they were typing gone. Observed for real on 2026-07-31 when the
 * backend container was recreated under an idle tab.
 *
 * On an on-prem box the operator restarts containers — for a deploy, a config
 * change, a reboot — so this is routine, not exotic.
 */
type RefreshOutcome =
  | { status: "ok"; token: string }
  | { status: "rejected" }
  | { status: "unreachable" };

let refreshing: Promise<RefreshOutcome> | null = null;

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const original = error.config as
      | (InternalAxiosRequestConfig & { _retried?: boolean })
      | undefined;
    if (
      error.response?.status === 401 &&
      original &&
      !original._retried &&
      !original.url?.endsWith("/auth/refresh")
    ) {
      original._retried = true;
      refreshing ??= refreshAccessToken().finally(() => {
        refreshing = null;
      });
      const outcome = await refreshing;
      if (outcome.status === "ok") {
        original.headers.set("Authorization", `Bearer ${outcome.token}`);
        return apiClient.request(original);
      }
      if (outcome.status === "unreachable") {
        // Keep the session. The backend is momentarily gone, not saying no.
        // This request fails and the caller surfaces it; the next attempt
        // refreshes again once the backend is back.
        return Promise.reject(error);
      }
      useAuthStore.getState().clearAuth();
      // Hard navigation rather than react-router push: the in-flight
      // request stack is unrecoverable once the session is gone, and
      // a full reload guarantees no stale Query cache leaks across
      // identities.
      // Both of these are raw browser navigation, NOT react-router, so the
      // basename does not apply and the prefix must be spelled out. The
      // guard has to match the prefixed path too — against a bare "/login"
      // it is never true under /sonoki/, so this would re-assign on every
      // 401 the login page itself sees.
      if (
        typeof window !== "undefined" &&
        !window.location.pathname.startsWith(LOGIN_PATH)
      ) {
        window.location.assign(LOGIN_PATH);
      }
    }
    return Promise.reject(error);
  },
);

/**
 * Rotate the session on purpose, rather than in response to a 401.
 *
 * WHY THIS EXISTS. The refresh cookie's lifetime IS the inactivity
 * window, and it only slides when something calls /auth/refresh. Before
 * 2026-08-25 that window was seven days and nothing needed to think
 * about it; it is half an hour now, and the candidate screens make NO
 * background requests at all -- no polling, and the application form
 * only saves while somebody is typing. So a candidate reading the bond
 * terms for half an hour would have made zero calls, and the next one would
 * have thrown them to the login screen mid-form.
 *
 * Reuses the same `refreshing` promise as the 401 path, because refresh
 * tokens are SINGLE-USE: two rotations in flight means the loser gets a
 * 401 and the session dies for no reason.
 *
 * It does not extend an ABANDONED tab in any meaningful way -- the idle
 * timer logs out at 15 minutes and the absolute cap ends the session at
 * 12 hours regardless. What it extends is a tab somebody is sitting in
 * front of.
 */
export async function keepSessionAlive(): Promise<void> {
  refreshing ??= refreshAccessToken().finally(() => {
    refreshing = null;
  });
  // The outcome is deliberately ignored. A rejection is handled the next
  // time a real request runs -- there is no user action to interrupt
  // here, and logging somebody out from a background timer is exactly
  // the behaviour the 401 path was rewritten to avoid.
  await refreshing;
}

async function refreshAccessToken(): Promise<RefreshOutcome> {
  try {
    const { data } = await apiClient.post<{ access_token: string }>(
      "/auth/refresh",
    );
    useAuthStore.getState().setAccessToken(data.access_token);
    return { status: "ok", token: data.access_token };
  } catch (err) {
    const status = (err as AxiosError).response?.status;
    // Only the server explicitly refusing the refresh token means the session
    // is over. Anything else — no response at all, a gateway error while the
    // container restarts — is transient, and logging the user out for it
    // would lose their unsaved work for no reason.
    if (status === 401 || status === 403) {
      return { status: "rejected" };
    }
    return { status: "unreachable" };
  }
}
